import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Download, HeartHandshake, CalendarClock, BadgeCheck, BarChart3, Users, Mail, Building2, Smile, ClipboardCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/api";
import axios from "axios";

const EMP_IMG = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=80";

export default function ForEmployers() {
  const [quote, setQuote] = useState({
    company_name: "",
    full_name: "",
    email: "",
    phone: "",
    roles_needed: "",
    urgency: "Within 30 days",
    message: "",
  });
  const [submittingQuote, setSubmittingQuote] = useState(false);

  const [leadEmail, setLeadEmail] = useState("");
  const [leadName, setLeadName] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);

  const handleQuote = async (e) => {
    e.preventDefault();
    if (!quote.company_name || !quote.email || !quote.full_name || !quote.roles_needed) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmittingQuote(true);
    try {
      await axios.post(`${API}/employers/quote`, quote);
      toast.success("Thanks! A recruiter will reach out within 1 business day.");
      setQuote({ company_name: "", full_name: "", email: "", phone: "", roles_needed: "", urgency: "Within 30 days", message: "" });
    } catch {
      toast.error("Unable to submit. Please try again.");
    } finally {
      setSubmittingQuote(false);
    }
  };

  const handleLead = async (e) => {
    e.preventDefault();
    if (!leadEmail || !leadName) {
      toast.error("Name and email are required.");
      return;
    }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        full_name: leadName,
        email: leadEmail,
        company: leadCompany,
        magnet: "salary_report",
      });
      const link = document.createElement("a");
      link.href = `${API}/downloads/salary-report`;
      link.setAttribute("download", "2026-Payroll-Salary-Benchmark-Report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report sent. Check your downloads.");
      setLeadName(""); setLeadEmail(""); setLeadCompany("");
    } catch {
      toast.error("Unable to send report. Try again.");
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-36 pb-24 bg-navy text-white overflow-hidden" data-testid="employers-hero">
        <div className="absolute inset-0 hero-grid opacity-25" />
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#10B981]/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#10B981]" />
              <span className="eyebrow text-[#10B981]">For Employers</span>
            </div>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
              Reliable support for<br /><em className="text-[#10B981] not-italic">the day-to-day</em>.
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              We find the hardworking assistants who handle timesheets, data entry, and employee questions —
              so your senior team doesn't have to. Friendly, dependable, entry-to-mid people who grow with you.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "Dependable", v: "Day-to-day support" },
                { k: "Friendly", v: "Handoff to your team" },
                { k: "22d", v: "Avg. time-to-fill" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-serif text-2xl text-white">{s.k}</div>
                  <div className="mt-1 text-xs uppercase tracking-widest text-white/55">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-2xl">
              <img src={EMP_IMG} alt="Team collaborating in office" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* OUR MATCHING PROCESS */}
      <section className="py-24 bg-ivory" data-testid="employers-vetting">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow text-[#10B981]">Our Matching Process</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-[1.05]">
              We screen for the qualities that matter most in day-to-day payroll work.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { t: "Attention to Detail", d: "A 30-minute exercise on entry accuracy, spotting mistakes, and handling a mock timesheet.", i: ClipboardCheck },
              { t: "Reliability", d: "Reference checks focused on follow-through, communication, and consistent attendance in past roles.", i: BadgeCheck },
              { t: "Basic Numeracy Skills", d: "Comfort with Excel formulas, percentages, and simple reconciliation — no CPA required.", i: BarChart3 },
              { t: "Friendly Communication", d: "Can they explain a pay-stub question kindly? We listen for tone and clarity, not jargon.", i: Smile },
              { t: "Willingness to Learn", d: "The best hires often started as office admins. We screen for curiosity and openness to training.", i: HeartHandshake },
              { t: "Team Fit", d: "A short conversation focused on how they like to work with teammates, not a pressure-test interview.", i: Users },
            ].map((c, idx) => (
              <div key={idx} className="card-lux" data-testid={`vetting-${idx}`}>
                <c.i className="h-6 w-6 text-[#10B981]" strokeWidth={1.8} />
                <h4 className="mt-5 font-serif text-2xl text-navy">{c.t}</h4>
                <p className="mt-3 text-navy/65 leading-relaxed text-sm">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST A QUOTE */}
      <section className="py-28 bg-white" id="quote" data-testid="employers-quote-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <div className="eyebrow text-[#10B981]">Request a Quote</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-navy leading-[1.05]">
              Tell us about the role.
            </h2>
            <p className="mt-6 text-navy/65 text-lg leading-relaxed">
              A quick form — we'll call within one business day with a shortlist plan and realistic timing.
              No long sales calls.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { i: Users, t: "Direct-hire only", d: "Full-time salaried placements. No staffing markups." },
                { i: CalendarClock, t: "Shortlist in 10 days", d: "Friendly, pre-screened candidates — not a firehose." },
                { i: HeartHandshake, t: "Long-term fit", d: "We care about the match sticking past year one." },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-md bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                    <f.i className="h-5 w-5 text-[#10B981]" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="font-serif text-xl text-navy">{f.t}</div>
                    <div className="text-sm text-navy/60 mt-1">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={handleQuote} className="card-lux p-10 space-y-5" data-testid="employer-quote-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="company">Company Name *</Label>
                  <Input id="company" data-testid="quote-company" className="mt-2" placeholder="Acme Finance, Inc." value={quote.company_name}
                    onChange={(e) => setQuote({ ...quote, company_name: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="qname">Your Name *</Label>
                  <Input id="qname" data-testid="quote-name" className="mt-2" placeholder="Jane Kim" value={quote.full_name}
                    onChange={(e) => setQuote({ ...quote, full_name: e.target.value })} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="qemail">Work Email *</Label>
                  <Input id="qemail" type="email" data-testid="quote-email" className="mt-2" placeholder="jane@acme.com" value={quote.email}
                    onChange={(e) => setQuote({ ...quote, email: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="qphone">Phone</Label>
                  <Input id="qphone" data-testid="quote-phone" className="mt-2" placeholder="(212) 555-0130" value={quote.phone}
                    onChange={(e) => setQuote({ ...quote, phone: e.target.value })} />
                </div>
              </div>

              <div>
                <Label htmlFor="roles">Roles Needed *</Label>
                <Input id="roles" data-testid="quote-roles" className="mt-2" placeholder="Payroll Assistant, Office Admin, Coordinator…" value={quote.roles_needed}
                  onChange={(e) => setQuote({ ...quote, roles_needed: e.target.value })} />
              </div>

              <div>
                <Label>Urgency of Hire</Label>
                <Select value={quote.urgency} onValueChange={(v) => setQuote({ ...quote, urgency: v })}>
                  <SelectTrigger className="mt-2" data-testid="quote-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Immediate (within 2 weeks)">Immediate (within 2 weeks)</SelectItem>
                    <SelectItem value="Within 30 days">Within 30 days</SelectItem>
                    <SelectItem value="Within 60 days">Within 60 days</SelectItem>
                    <SelectItem value="Exploratory / building short-list">Exploratory / building short-list</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="qmsg">Anything we should know?</Label>
                <Textarea id="qmsg" data-testid="quote-message" rows={4} className="mt-2" placeholder="Tell us about the team, the tools they use, and what a great hire looks like…" value={quote.message}
                  onChange={(e) => setQuote({ ...quote, message: e.target.value })} />
              </div>

              <button type="submit" disabled={submittingQuote} className="btn-emerald w-full disabled:opacity-70" data-testid="quote-submit-btn">
                {submittingQuote ? "Sending…" : "Request My Quote"}
                {!submittingQuote && <ArrowRight className="h-4 w-4" />}
              </button>
              <p className="text-xs text-navy/45 text-center">No spam. No third-party sharing. One friendly follow-up.</p>
            </form>
          </div>
        </div>
      </section>

      {/* SALARY REPORT LEAD MAGNET */}
      <section className="py-28 bg-navy text-white relative overflow-hidden" id="report" data-testid="salary-report-section">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="absolute -bottom-40 -right-20 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/8 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="eyebrow text-[#D4AF37]">Free Resource</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05]">
              The 2026 Payroll Salary Benchmark Report.
            </h2>
            <p className="mt-5 text-white/75 text-lg leading-relaxed max-w-xl">
              What Payroll Assistants and support staff are actually being paid in 2026. Median ranges for
              entry, mid, and senior roles — plus what premium skills add to the package.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div><BarChart3 className="h-5 w-5 text-[#D4AF37]" /><div className="mt-2 text-xs uppercase tracking-widest text-white/60">6 roles</div></div>
              <div><Building2 className="h-5 w-5 text-[#D4AF37]" /><div className="mt-2 text-xs uppercase tracking-widest text-white/60">US markets</div></div>
              <div><Download className="h-5 w-5 text-[#D4AF37]" /><div className="mt-2 text-xs uppercase tracking-widest text-white/60">Instant PDF</div></div>
            </div>
          </div>

          <div className="md:col-span-6">
            <form onSubmit={handleLead} className="rounded-2xl bg-white p-10 shadow-2xl" data-testid="salary-report-form">
              <div className="eyebrow text-[#10B981]">Download · Free</div>
              <h3 className="mt-3 font-serif text-3xl text-navy">Get the report</h3>
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="lname" className="text-navy/70">Full Name</Label>
                  <Input id="lname" data-testid="lead-name" className="mt-2" value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Jane Kim" />
                </div>
                <div>
                  <Label htmlFor="lemail" className="text-navy/70">Work Email</Label>
                  <Input id="lemail" type="email" data-testid="lead-email" className="mt-2" value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="jane@acme.com" />
                </div>
                <div>
                  <Label htmlFor="lcompany" className="text-navy/70">Company</Label>
                  <Input id="lcompany" data-testid="lead-company" className="mt-2" value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)} placeholder="Acme Finance" />
                </div>
              </div>
              <button type="submit" disabled={leadLoading} className="btn-gold w-full mt-6 disabled:opacity-70" data-testid="lead-download-btn">
                {leadLoading ? "Preparing…" : "Download Report"}
                {!leadLoading && <Download className="h-4 w-4" />}
              </button>
              <p className="text-xs text-navy/45 text-center mt-3 flex items-center justify-center gap-2">
                <Mail className="h-3 w-3" /> Delivered as PDF. No follow-up spam.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
